import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useBilling } from "@/hooks/useBilling";
import { useConfirm } from "@/context/ConfirmContext";
import { Plus, Trash2, BookmarkPlus, Lock, Sparkles, Share2, Copy } from "lucide-react";

const TRADES = ["General Renovation", "Masonry", "Electrical", "Plumbing", "Painting", "Roofing", "Flooring", "Exterior Work", "Apartment Turnover", "Other"];
const QUOTE_TYPES = ["Estimate", "Invoice", "Scope of Work", "Change Order"];
const TONES = ["Simple", "Professional", "Detailed", "Premium", "Short and direct"];

const empty = {
  name: "",
  trade_category: "General Renovation",
  quote_type: "Estimate",
  tone: "Professional",
  num_items: 3,
  itemized: true,
  rough_notes_template: "",
  special_notes: "",
  exclusions: "",
};

function UpgradeGate() {
  return (
    <div className="max-w-2xl mx-auto" data-testid="templates-upgrade-gate">
      <Card className="p-10 text-center border-orange-200 bg-orange-50/50">
        <div className="h-12 w-12 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center mx-auto mb-4">
          <Lock className="h-5 w-5" />
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Saved trade templates</h1>
        <p className="text-slate-600 mt-2 max-w-md mx-auto">
          Save your most-used quote setups by trade and load them in one click. Available on the Pro and Crew plans.
        </p>
          <Link to="/pricing" data-testid="templates-upgrade-cta">
            <Button className="mt-6 bg-orange-500 hover:bg-orange-600 text-white">
              <Sparkles className="h-4 w-4 mr-2" /> Upgrade to unlock
            </Button>
          </Link>
      </Card>
    </div>
  );
}

export default function TemplatesPage() {
  const { subscription, loading: billingLoading } = useBilling();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // template object or null
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const confirm = useConfirm();

  const hasAccess = subscription?.status === "active" && ["pro", "crew"].includes(subscription?.plan);

  const load = () => api.get("/templates").then((r) => setTemplates(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    if (hasAccess) load();
    else setLoading(false);
  }, [hasAccess]);

  if (billingLoading) return <div className="text-slate-400 py-12 text-center">Loading…</div>;
  if (!hasAccess) return <UpgradeGate />;

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ ...empty, ...t });
    setOpen(true);
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, num_items: Number(form.num_items) || 3 };
      if (editing) {
        await api.put(`/templates/${editing.id}`, payload);
        toast.success("Template updated.");
      } else {
        await api.post("/templates", payload);
        toast.success("Template saved.");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete this template?",
      description: "Existing quotes that used it are not affected.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/templates/${id}`);
    toast.success("Deleted.");
    load();
  };

  const share = async (id) => {
    setShareLoading(true);
    setShareOpen(true);
    try {
      const r = await api.post(`/templates/${id}/share`);
      const full = `${window.location.origin}${r.data.url}`;
      setShareUrl(full);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate share link");
      setShareOpen(false);
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareUrl = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied.");
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="templates-page">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Trade templates</h1>
          <p className="text-slate-500 mt-1">Reusable starting points for your most common jobs.</p>
        </div>
        <Button onClick={openCreate} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="add-template-button">
          <Plus className="h-4 w-4 mr-2" /> New template
        </Button>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-400">Loading…</Card>
      ) : templates.length === 0 ? (
        <Card className="p-12 text-center border-dashed border-slate-300">
          <BookmarkPlus className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No templates yet. Save your first one to speed up future quotes.</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {templates.map((t) => (
            <Card key={t.id} className="p-5 border-slate-200" data-testid={`template-card-${t.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <button
                    className="font-medium text-slate-900 text-left hover:text-orange-600 truncate w-full"
                    onClick={() => openEdit(t)}
                    data-testid={`edit-template-${t.id}`}
                  >
                    {t.name}
                  </button>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className="border-slate-200 text-slate-600">{t.trade_category}</Badge>
                    <Badge variant="outline" className="border-slate-200 text-slate-600">{t.quote_type}</Badge>
                  </div>
                  {t.rough_notes_template && (
                    <p className="text-xs text-slate-500 mt-2 line-clamp-2">{t.rough_notes_template}</p>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => share(t.id)} data-testid={`share-template-${t.id}`} title="Share">
                    <Share2 className="h-4 w-4 text-slate-500" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(t.id)} data-testid={`delete-template-${t.id}`}>
                    <Trash2 className="h-4 w-4 text-rose-500" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit template" : "New template"}</DialogTitle>
            <DialogDescription>Pre-fill the New Quote form with this setup.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template name</Label>
              <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="e.g. Standard bathroom reno" data-testid="template-name-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Trade</Label>
                <Select value={form.trade_category} onValueChange={(v) => update("trade_category", v)}>
                  <SelectTrigger data-testid="template-trade"><SelectValue /></SelectTrigger>
                  <SelectContent>{TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quote type</Label>
                <Select value={form.quote_type} onValueChange={(v) => update("quote_type", v)}>
                  <SelectTrigger data-testid="template-quote-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{QUOTE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tone</Label>
                <Select value={form.tone} onValueChange={(v) => update("tone", v)}>
                  <SelectTrigger data-testid="template-tone"><SelectValue /></SelectTrigger>
                  <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label># default line items</Label>
                <Input type="number" min="1" max="15" value={form.num_items} onChange={(e) => update("num_items", e.target.value)} data-testid="template-num-items" />
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
              <Label className="m-0">Itemized by default</Label>
              <Switch checked={form.itemized} onCheckedChange={(v) => update("itemized", v)} data-testid="template-itemized" />
            </div>
            <div>
              <Label>Starter rough notes</Label>
              <Textarea
                value={form.rough_notes_template}
                onChange={(e) => update("rough_notes_template", e.target.value)}
                placeholder="Pre-filled notes you usually start with for this trade…"
                className="min-h-[120px]"
                data-testid="template-rough-notes"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Default special notes</Label>
                <Textarea value={form.special_notes} onChange={(e) => update("special_notes", e.target.value)} className="min-h-[80px]" data-testid="template-special-notes" />
              </div>
              <div>
                <Label>Default exclusions</Label>
                <Textarea value={form.exclusions} onChange={(e) => update("exclusions", e.target.value)} className="min-h-[80px]" data-testid="template-exclusions" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="save-template-button">
              {saving ? "Saving…" : (editing ? "Update template" : "Save template")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share link dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Share template</DialogTitle>
            <DialogDescription>
              Anyone with this link can add a copy of this template to their account (Pro/Crew plan required).
              The link expires in 30 days.
            </DialogDescription>
          </DialogHeader>
          {shareLoading ? (
            <div className="py-4 text-center text-slate-500">Generating link…</div>
          ) : (
            <div className="flex gap-2">
              <Input value={shareUrl} readOnly data-testid="share-url-input" className="font-mono text-xs" />
              <Button onClick={copyShareUrl} data-testid="copy-share-url" className="bg-slate-900 hover:bg-slate-800 text-white shrink-0">
                <Copy className="h-4 w-4 mr-2" /> Copy
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
